import * as prismic from '@prismicio/client';
import DOMPurify from 'isomorphic-dompurify';
import Cookies from 'js-cookie';
import { GetServerSidePropsContext, NextApiRequest } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { v4 as uuidv4 } from 'uuid';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getSelectorsByUserAgent } from 'react-device-detect';
import { renderToString } from 'react-dom/server';
import type { InstantSearchProps } from 'react-instantsearch';
import {
  InstantSearchSSRProvider,
  InstantSearchServerState,
  getServerState,
} from 'react-instantsearch';

import {
  ALGONOMY_TOP_CATEGORY_EN,
  ALGONOMY_TOP_CATEGORY_FR,
} from '@adapters/algonomy/constants';
import getCategoryBreadcrumbs from '@adapters/bigcommerce/operations/categories/getCategoryBreadcrumbs';
import getLoggedInCustomer from '@adapters/bigcommerce/operations/customer/getLoggedInCustomer';
import { ALGOLIA_CHECKOUT_QUERY_ID_NAME } from '@adapters/bigcommerce/constants';
import { getCollectionByCategoryPath } from '@adapters/prismic/fetching/plp';
import getProductTagInfoData from '@adapters/prismic/fetching/productTagInfo/getProductTagInfo';
import getAllPromoTickerData from '@adapters/prismic/fetching/promos/getAllPromoTickerData';
import getBannerStripGlobalData from '@adapters/prismic/fetching/banner-strip-global/getBannerStripGlobalData';
import getSeoComponentByUid from '@adapters/prismic/fetching/seoComponent/getSeoComponentByPath';
import { getAllStoresDataForSetMyStore } from '@adapters/prismic/fetching/stores';
import {
  CollectionData,
  PlpCrossLinkingCmsData,
} from '@adapters/prismic/types/plp';
import { getPreviewRef } from '@adapters/prismic/utils/getPreviewRef';
import { repositoryName } from '@adapters/prismic/repositoryName';

import { removeKnownFilterFromCategoryPath } from '@utils/algolia/urlIsKnownFilter';
import { getCategoryNameFromSlug } from '@utils/algolia/getCategoryNameFromSlug';
import { getCategorySlugsFromUrl } from '@utils/algolia/getCategorySlugsFromUrl';
import { knownFacetNames } from '@utils/algolia/knownFacetNames';
import { getIndex } from '@utils/algolia/analytics';
import { generateAlgoliaSearchKey } from '@utils/algolia/securedKey';
import {
  parseAlgoliaUrl,
  shouldCanonicalToBaseBrand,
  baseBrandCanonicalPath,
} from '@utils/algolia/helpers';
import { isActiveCategory } from '@utils/algolia/isActiveCategory';
import { MEC_TITLE } from '@utils/constants';
import {
  getGroupKeyByCustomerGroupId,
  PriceGroupKey,
} from '@utils/selectPriceByGroup';
import { LocaleOverrideContext } from '@hooks/useLocale';
import { SsrPriceGroupContext } from '@hooks/usePriceGroupKey';
import getPageLocale from '@utils/getPageLocale';
import { altLocale } from '@utils/locale';
import { generateAltURL } from '@utils/plp';
import { BRANDSREGEX } from '@utils/plp/constants';
import { getEnH1AndPageTitle } from '@utils/plp/getEnH1AndPageTitle';
import { getFrH1AndPageTitle } from '@utils/plp/getFrH1AndPageTitle';
import { titleCase } from '@utils/plp/utils/transforms';
import transformLocale from '@utils/transform-locale';
import { sanitizeSearchParams } from '@utils/url';
import Error404Message from '@components/404';
import SearchResults from '@components/algolia/SearchResults';
import Breadcrumbs from '@components/algolia/Breadcrumbs';
import CategoryBreadcrumbs from '_components/Breadcrumbs/CategoryBreadcrumbs';
import breadcrumbOverrides from '_components/Breadcrumbs/Breadcrumbs.pageOverrides.module.scss';
import { capitalize } from '@utils/plp/utils/transforms';

import Container from '@components/global/Container';
import Layout from '@components/global/Layout';
import { AlgonomyPlacement } from '@components/global/Recommendations/Loader';
import SeoContentAccordion from '_components/PLP/SeoContentAccordion';
import mapSeoAccordionData, {
  SeoAccordionData,
} from '@adapters/prismic/utils/mapSeoAccordionData';
import SeoContentComponent, {
  SeoContentData,
} from '@components/global/SeoContentComponent/SeoContentComponent';
import { StructuredBrandData } from '@components/product/StructuredSearchData';
import {
  FEATURE_FLAG_BREADCRUMBS_V2,
  FEATURE_FLAG_HIDE_LEGACY_PLP_COLLECTION_CONTENT,
  FEATURE_FLAG_PLP_FULL_WIDTH,
  FEATURE_FLAG_PLP_HERO,
  FEATURE_FLAG_PROMO_BAR_SERVER_SIDE,
  FEATURE_FLAG_SEO_ACCORDION,
  getAlgonomyPathFeatureFlag,
} from '@config/feature-flag';
import { handlePromoDetailsTracking } from '@utils/gtm';
import { preparePathForCache } from '@utils/plp/utils/preparePathForCache';
import BrandedStoreSliceZone from '@components/cms/BrandedStoreSliceZone';
import { BrandedStoreSliceType } from '@adapters/prismic/types/brandedStorePage';
import EnhancedPlpBanner from '@components/algolia/EnhancedPlpBanner';
import PlpHero from '_components/PlpHero';
import { PlpHeroData } from '_components/PlpHero/types';
import type { PlpCrossLinkingData } from '_components/PlpCrossLinking/types';
import { collectPlpCrossLinkingProductIds } from '@adapters/prismic/transforms/plpCrossLinking';
import { classifyUiStateChange } from '@utils/algolia/uiStateChange';
import getProductsByProductCodes from '@adapters/algolia/getProductsByProductCodes';
import ProductStripDeals from '_components/Products/ProductStripDeals';

const RecommendationsLoader = dynamic(
  () => import('@components/global/Recommendations/Loader'),
  {
    loading: () => null,
    ssr: false,
  }
);

const CompareBar = dynamic(() => import('_components/CompareBar'), {
  loading: () => null,
});

import styles from './plp.module.scss';
import getPromoBarData from '@adapters/prismic/fetching/promos/getPromoBarData';

const LOCALE_PAGE_PATHS = {
  en: 'products',
  fr: 'produits',
};

const PRISMIC_BUILDER_URL = `https://${repositoryName}.prismic.io/builder/pages`;

type PlpPageProps = {
  serverState?: InstantSearchServerState;
  serverUrl: string;
  pageTitle: string;
  /** `<title>` text; merch's collection page title overrides it on the unfiltered PLP (ED-449). */
  seoPageTitle: string;
  collectionData: CollectionData;
  canonicalUrl: string;
  xDefault: string;
  alternateLocaleUrl: string;
  isMobile: boolean;
  // Shape is chosen by FEATURE_FLAG_SEO_ACCORDION: the accordion shape when on,
  // the legacy SeoContentComponent shape when off (both built in gSSP below).
  seoContentData?: SeoAccordionData | SeoContentData | null;
  algonomyCategoryId: string | null;
  collectionSlug?: string;
  heroTitle?: string;
  algoliaQueryID: string | null;
  priceGroupKey: PriceGroupKey;
  plpHeroData: PlpHeroData | null;
  /**
   * Cross-linking band (ED-211). Separate from `collectionData` because its
   * product records are resolved per request rather than served from the
   * long-lived collection cache, so prices are never stale.
   */
  crossLinking: PlpCrossLinkingData | null;
};

export default function PlpPage({
  serverUrl,
  serverState,
  pageTitle,
  seoPageTitle,
  collectionData,
  canonicalUrl,
  alternateLocaleUrl,
  seoContentData,
  algonomyCategoryId,
  isMobile,
  collectionSlug,
  heroTitle,
  algoliaQueryID,
  xDefault,
  priceGroupKey,
  plpHeroData,
  crossLinking,
}: PlpPageProps) {
  const { t } = useTranslation('searchResults');
  const { query, asPath } = useRouter();
  const locale = (query.locale as string) || 'en';

  const localePagePath =
    LOCALE_PAGE_PATHS[(locale ?? 'en') as keyof typeof LOCALE_PAGE_PATHS];

  const { isBrandPlp, brandIdentifier, isCollectionPlp } = parseAlgoliaUrl(
    serverUrl,
    locale
  );

  const breadcrumbCategories = useMemo(() => {
    const categoryNames =
      collectionSlug === localePagePath
        ? []
        : collectionSlug?.split('/').map((slug, index, arr) => {
            const name = getCategoryNameFromSlug(slug, locale);
            return isCollectionPlp && index === arr.length - 1
              ? name.replace(/-/g, ' ')
              : name;
          }) ?? [];

    return [capitalize(localePagePath), ...categoryNames];
  }, [localePagePath, collectionSlug, locale, isCollectionPlp]);
  const [updatedPageTitle, setUpdatedPageTitle] = useState<string>(pageTitle);
  const [updatedSeoTitle, setUpdatedSeoTitle] = useState<string>(seoPageTitle);
  const [plpCollectionData, setPlpCollectionData] =
    useState<CollectionData | null>(collectionData);
  // Refinements applied the last time `onSearchStateChange` ran, so paging can
  // be told apart from refining — see the comment on `isPaginationOnly`.
  const lastRefinementSignature = useRef<string | null>(null);
  type CollectionFilters = [string, unknown][];

  const displayCoopComponents =
    !FEATURE_FLAG_HIDE_LEGACY_PLP_COLLECTION_CONTENT &&
    process.env.NEXT_PUBLIC_DISPLAY_COOP_COMPONENTS === 'true' &&
    (plpCollectionData?.brandedStoreSlices?.length ?? 0) !== 0
      ? true
      : false;

  // plpHeroData is already env-flag gated in getServerSideProps; the stateful
  // plpCollectionData check hides the hero on client-side facet refinement.
  const displayPlpHero = !!plpHeroData && !!plpCollectionData?.plpHeroData;
  const displayDealsStrip = !!plpCollectionData?.productStripDeals?.length;

  const displayEnhancedPlpBanner =
    !FEATURE_FLAG_HIDE_LEGACY_PLP_COLLECTION_CONTENT &&
    process.env.NEXT_PUBLIC_DISPLAY_ENHANCED_PLP_BANNER === 'true' &&
    plpCollectionData?.enhancedPlpBanner?.title &&
    !displayPlpHero
      ? true
      : false;

  const algoliaDebug = Cookies.get('algoliaDebug');
  if (algoliaDebug && collectionData?.prismicId) {
    console.log(
      `Prismic: Collection ${collectionData?.prismicId} ${PRISMIC_BUILDER_URL}/${collectionData?.prismicId}`
    );
  }

  if (algoliaDebug && collectionData?.plpHeroData?.prismicId) {
    console.log(
      `Prismic: PLP hero ${collectionData.plpHeroData.prismicId} ${PRISMIC_BUILDER_URL}/${collectionData.plpHeroData.prismicId}`
    );
  }

  // The band's product records come from getServerSideProps; the stateful
  // plpCollectionData check hides it on client-side facet refinement, matching
  // the hero, PTTs, and every other CMS-sourced element on this page.

  const displayCrossLinking = plpCollectionData?.plpCrossLinking
    ? crossLinking
    : null;
  const pttTiles = collectionData?.plpPttData ?? [];
  const productLineCarousel =
    collectionData?.productLineCarousel?.items?.length > 1
      ? collectionData?.productLineCarousel
      : null;

  const [collectionFilters] = useState<CollectionFilters>(() =>
    !asPath.includes('?filters')
      ? []
      : [
          [
            'placeholder to not ensure',
            'banner only displays if no query present on load',
          ],
        ]
  );

  const [recommendations, setRecommendations] = useState<AlgonomyPlacement[]>(
    []
  );
  const [brandRecommendations, setBrandRecommendations] = useState<
    AlgonomyPlacement[]
  >([]);
  const title = `${updatedSeoTitle || seoPageTitle} ${MEC_TITLE}`;

  const description =
    typeof plpCollectionData?.seoDescription === 'string' &&
    plpCollectionData.seoDescription.trim() !== ''
      ? plpCollectionData.seoDescription
      : t('collection.description', { name: seoPageTitle });

  useEffect(() => {
    handlePromoDetailsTracking('promoDetails');
  }, []);

  useEffect(() => {
    if (algoliaQueryID) {
      Cookies.set(ALGOLIA_CHECKOUT_QUERY_ID_NAME, algoliaQueryID);
    }
  }, [algoliaQueryID]);

  const noProductLineCarouselItems =
    plpCollectionData?.productLineCarousel === null ||
    !plpCollectionData?.productLineCarousel?.items?.every(
      (item) => item?.title !== null
    );

  const displayBanner = useMemo(
    () =>
      (noProductLineCarouselItems &&
        (plpCollectionData?.plpBannerData?.heroImage?.url ||
          (Array.isArray(plpCollectionData?.plpMerchBoxesData?.boxes) &&
            plpCollectionData?.plpMerchBoxesData?.boxes?.length !== 0)) &&
        collectionFilters.length === 0) || // we will only display the banner / merchboxes on root level of collections, ie when no filters are applied
      !!plpCollectionData?.productTagInfoBannerData?.tagName ||
      plpCollectionData?.categoryCarousels?.length !== 0,
    [
      plpCollectionData?.plpBannerData?.heroImage?.url,
      plpCollectionData?.plpMerchBoxesData?.boxes,
      plpCollectionData?.productTagInfoBannerData?.tagName,
      plpCollectionData?.categoryCarousels?.length,
      collectionFilters.length,
      noProductLineCarouselItems,
    ]
  );

  // The deals grid's large card is only the LCP candidate when no image
  // content renders above it; otherwise prioritising it competes with the
  // hero or banner that actually is.
  const dealsGridPriority =
    !displayCoopComponents &&
    !displayEnhancedPlpBanner &&
    !displayPlpHero &&
    productLineCarousel === null &&
    !displayBanner;

  const searchResultsTitle = updatedPageTitle || pageTitle;

  const indexName = getIndex(locale);

  const onSearchStateChange: InstantSearchProps['onStateChange'] = ({
    uiState,
    setUiState,
  }) => {
    const {
      hierarchicalMenu: { 'categories.lvl0': categories = [] } = {},
      refinementList: {
        brand = [],
        gender = [],
        promotions = [],
        'facets.Ideal for': idealFor = [],
        'facets.Collection': collection = [],
      } = {},
    } = uiState[indexName];

    const { signature, isPaginationOnly } = classifyUiStateChange(
      uiState[indexName],
      lastRefinementSignature.current
    );

    lastRefinementSignature.current = signature;

    const resetCollectionData = () => {
      if (!isPaginationOnly) {
        setPlpCollectionData(null);
      }
    };

    // Check if ProductLineCarousel is present and only Collection filters are applied
    const hasProductLineCarousel =
      productLineCarousel !== null && productLineCarousel?.items?.length > 0;
    const hasOnlyCollectionFilters =
      collection.length > 0 &&
      brand.length === 0 &&
      gender.length === 0 &&
      promotions.length === 0 &&
      idealFor.length === 0;

    // Check if no filters are applied at all
    const hasNoFilters =
      collection.length === 0 &&
      brand.length === 0 &&
      gender.length === 0 &&
      promotions.length === 0 &&
      idealFor.length === 0;

    // If ProductLineCarousel is present and either only Collection filters are applied
    // OR no filters are applied (after removing Collection filters),
    // preserve the original page title instead of generating a new one
    if (hasProductLineCarousel && (hasOnlyCollectionFilters || hasNoFilters)) {
      setUpdatedPageTitle(pageTitle); // Keep the original page title
      setUpdatedSeoTitle(seoPageTitle);
      setUiState(uiState);
      resetCollectionData();
      setRecommendations([]);
      return;
    }

    // Here we're building a mocked url from facets to pass into
    // the existing title builder

    const baseCategoryTitle = categories.at(-1) || '';

    const titleUrl = new URL(
      `${window.location.origin}${window.location.pathname}`
    );
    if (brand.length === 1) {
      titleUrl.searchParams.append('brand', brand[0]);
    }

    if (gender.length === 1) {
      titleUrl.searchParams.append('genders', gender[0]);
    }

    if (promotions.length === 1) {
      titleUrl.searchParams.append('promotions', promotions[0]);
    }

    if (idealFor.length === 1) {
      titleUrl.searchParams.append('ideal for', idealFor[0]);
    }

    const updatedTitle =
      locale === 'fr'
        ? getFrH1AndPageTitle(
            titleUrl.toString(),
            baseCategoryTitle.toLocaleLowerCase()
          )
        : getEnH1AndPageTitle(
            titleUrl.toString(),
            baseCategoryTitle.toLocaleLowerCase()
          );

    // Preserve breadcrumbs from the URL path; don't override with Algolia's
    // hierarchical menu which may be incomplete when facets are applied.
    setUpdatedPageTitle(updatedTitle);
    setUpdatedSeoTitle(updatedTitle);
    setUiState(uiState);
    resetCollectionData();
    setRecommendations([]);
  };

  const algoliaHits =
    serverState?.initialResults[indexName]?.results?.[0]?.hits || [];

  if (Cookies.get('algoliaDebug')) {
    serverState?.initialResults[indexName]?.results?.[0].appliedRules?.forEach(
      (rule) =>
        console.log(
          `algolia: applied merch rule: https://dashboard.algolia.com/apps/${process.env.NEXT_PUBLIC_ALGOLIA_APP_ID}/rules/${indexName}/visual-editor/edit/${rule.objectID}`
        )
    );
  }

  if (!algoliaHits.length) {
    return <Error404Message />;
  }

  const brandName = brandIdentifier
    ? getCategoryNameFromSlug(brandIdentifier, locale)
    : null;

  const categoryCarouselProducts = recommendations.filter(
    (recommendation) =>
      recommendation.name === 'category_page.category1_rr' ||
      recommendation.name === 'category_page.category2_rr' ||
      recommendation.name === 'category_page.category3_rr'
  );

  const catNavCarouselProducts = plpCollectionData?.brandedStoreSlices?.filter(
    (slice) => slice.slice_type === BrandedStoreSliceType.categoryNavCarousel
  );

  return (
    <>
      <RecommendationsLoader
        pageType={brandName ? 'brand' : 'collection'}
        data={{
          brandName: brandName,
          categoryId: algonomyCategoryId,
        }}
        onLoad={(data) => {
          if (brandName) {
            setBrandRecommendations(data);
          } else {
            setRecommendations(data);
          }
        }}
        trigger={brandName !== undefined || algonomyCategoryId !== null}
      />

      <NextSeo
        title={locale === 'en' ? titleCase(title) : title}
        description={description}
        canonical={canonicalUrl}
        languageAlternates={[
          { hrefLang: altLocale(locale), href: alternateLocaleUrl },
          { hrefLang: String(locale), href: canonicalUrl },
          { hrefLang: 'x-default', href: xDefault },
        ]}
        openGraph={{ title, description, locale }}
      />

      {plpCollectionData?.brandedStoreSlices?.length &&
      displayCoopComponents ? (
        <BrandedStoreSliceZone
          slices={plpCollectionData?.brandedStoreSlices}
          brandRecommendations={brandRecommendations}
          brandedStoreCoopAttribute={
            plpCollectionData?.brandedStoreCoopAttribute
          }
        />
      ) : null}

      {displayEnhancedPlpBanner &&
        plpCollectionData?.enhancedPlpBanner?.title && (
          <>
            <EnhancedPlpBanner
              title={plpCollectionData?.enhancedPlpBanner?.title}
              copy={plpCollectionData?.enhancedPlpBanner?.copy}
              markdownCopy={plpCollectionData?.enhancedPlpBanner?.markdownCopy}
              ctaText={plpCollectionData?.enhancedPlpBanner?.ctaText}
              ctaUrl={plpCollectionData?.enhancedPlpBanner?.ctaUrl}
              desktopImage={plpCollectionData?.enhancedPlpBanner?.desktopImage}
              mobileImage={plpCollectionData?.enhancedPlpBanner?.mobileImage}
              brandedStoreCoopAttribute={
                plpCollectionData?.brandedStoreCoopAttribute
              }
            />
            <BrandedStoreSliceZone
              slices={catNavCarouselProducts}
              brandedStoreCoopAttribute={
                plpCollectionData?.brandedStoreCoopAttribute
              }
            />
          </>
        )}
      <div id={'plpContainer'} />
      <Container
        fullWidth={FEATURE_FLAG_PLP_FULL_WIDTH}
        className={styles.breadcrumbRow}
      >
        {FEATURE_FLAG_BREADCRUMBS_V2 ? (
          <CategoryBreadcrumbs
            breadcrumbCategories={breadcrumbCategories}
            className={breadcrumbOverrides.inContainer}
          />
        ) : (
          <Breadcrumbs breadcrumbCategories={breadcrumbCategories} />
        )}
      </Container>

      {displayPlpHero && plpHeroData && (
        <div className={styles.heroSection}>
          <PlpHero data={plpHeroData} locale={locale} />
        </div>
      )}

      <Container
        fullWidth={FEATURE_FLAG_PLP_FULL_WIDTH}
        className={styles.plpContainer}
      >
        {isBrandPlp && brandName && <StructuredBrandData brand={brandName} />}

        <InstantSearchSSRProvider {...serverState}>
          <SsrPriceGroupContext.Provider value={priceGroupKey}>
            <SearchResults
              serverUrl={serverUrl}
              locale={locale}
              title={
                !FEATURE_FLAG_HIDE_LEGACY_PLP_COLLECTION_CONTENT &&
                collectionData?.enhancedPlpBanner?.title
                  ? ''
                  : searchResultsTitle
              }
              displayBanner={displayEnhancedPlpBanner ? false : displayBanner}
              isMobile={isMobile}
              onStateChange={onSearchStateChange}
              pttTiles={pttTiles}
              productLineCarousel={productLineCarousel}
              recommendations={categoryCarouselProducts}
              collectionData={plpCollectionData}
              crossLinking={displayCrossLinking}
              displayCoopComponents={displayCoopComponents}
              heroTitle={heroTitle}
              productStripDeals={plpCollectionData?.productStripDeals}
              dealsGrid={plpCollectionData?.dealsGrid}
              dealsGridPriority={dealsGridPriority}
            />
          </SsrPriceGroupContext.Provider>
        </InstantSearchSSRProvider>
        <CompareBar />
      </Container>
      {FEATURE_FLAG_SEO_ACCORDION ? (
        <SeoContentAccordion data={seoContentData as SeoAccordionData | null} />
      ) : (
        <SeoContentComponent data={seoContentData as SeoContentData} />
      )}
    </>
  );
}
PlpPage.Layout = Layout;

export async function getServerSideProps({
  req,
  res,
  params,
  previewData,
  preview = false,
  resolvedUrl,
}: GetServerSidePropsContext) {
  const locale = params?.locale === 'fr' ? 'fr' : 'en';
  if (!locale) {
    throw new Error('No locale specified for page');
  }

  const notDefaultLocale = transformLocale(locale);

  const name = params?.name?.length
    ? params.name
    : LOCALE_PAGE_PATHS[notDefaultLocale as keyof typeof LOCALE_PAGE_PATHS];

  const alternateLocale = altLocale(notDefaultLocale) || 'fr';
  const splitUrl = resolvedUrl
    .replace(
      LOCALE_PAGE_PATHS[alternateLocale],
      LOCALE_PAGE_PATHS[notDefaultLocale as keyof typeof LOCALE_PAGE_PATHS]
    )
    .split('?');
  const queryString = new URLSearchParams(splitUrl?.[1]);
  const strippedQuery = sanitizeSearchParams(queryString, knownFacetNames);

  const pageLocale = getPageLocale(notDefaultLocale);

  let collectionSlug = Array.isArray(name)
    ? name.join('/')
    : LOCALE_PAGE_PATHS[pageLocale];

  const collectionSlugWithoutFilters = removeKnownFilterFromCategoryPath(
    collectionSlug,
    notDefaultLocale,
    '/'
  );

  const categoryPathWithoutFilters =
    collectionSlugWithoutFilters === LOCALE_PAGE_PATHS[pageLocale]
      ? `/${LOCALE_PAGE_PATHS[pageLocale]}/`
      : `/${LOCALE_PAGE_PATHS[pageLocale]}/${collectionSlugWithoutFilters}`;

  const brandsRegex = new RegExp(BRANDSREGEX);
  const matchedBrand = resolvedUrl?.match(brandsRegex);

  if (matchedBrand) {
    const isFilteredBrand = resolvedUrl?.split('/').length > 4;

    if (matchedBrand && !isFilteredBrand) {
      const brandName = matchedBrand?.[2];
      collectionSlug =
        notDefaultLocale === 'fr'
          ? `marques/${brandName}`
          : `brands/${brandName}`;
    }
  }

  const previewRef = getPreviewRef(preview, previewData);

  const cacheableCollectionPath = preparePathForCache(
    collectionSlug,
    strippedQuery,
    notDefaultLocale
  );

  // We know our active categories ahead of time, so we can use these to fail early
  // if the category isn't a valid one, and not even bother querying algolia
  const categoryPath =
    cacheableCollectionPath === `/${LOCALE_PAGE_PATHS[pageLocale]}`
      ? LOCALE_PAGE_PATHS[pageLocale]
      : LOCALE_PAGE_PATHS[pageLocale] +
          cacheableCollectionPath?.split('?')[0] || '';

  const {
    isBrandPlp,
    brandIdentifier,
    brandCategoryFilter,
    isFilteredBrand,
    isCollectionPlp,
  } = parseAlgoliaUrl(categoryPath, notDefaultLocale);

  const brandCategoryFilterFR = brandCategoryFilter
    ? `produits/${brandCategoryFilter}`
    : '';
  const brandCategoryFilterEN = brandCategoryFilter
    ? `products/${brandCategoryFilter}`
    : '';

  const brandCategoryFilterByLocale =
    notDefaultLocale === 'fr' ? brandCategoryFilterFR : brandCategoryFilterEN;

  const pathIsActiveCategory = isActiveCategory(
    brandCategoryFilterByLocale || categoryPath,
    notDefaultLocale
  );

  const userAgent = req.headers['user-agent'] || '';
  const { isMobile } = getSelectorsByUserAgent(userAgent);

  // Next.js typically strips the locale prefix from req.url, but some environments
  // (e.g. Vercel with a non-default locale) may still include it, so we check before prepending.
  const host = req?.headers?.host?.replace('bc-prod.', 'www.') || '';
  const protocol = host.includes('localhost') ? 'http' : 'https';

  const path = (req?.url || '').replace(/^\//, '');
  const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pathHasLocale = new RegExp(
    `^(${escapeRegex(notDefaultLocale)}|${escapeRegex(alternateLocale)})(\\/|$)`
  ).test(path);
  const requestUrl = pathHasLocale ? path : `${notDefaultLocale}/${path}`;
  const fullUrl = new URL(`${protocol}://${host}/${requestUrl}`);
  fullUrl.searchParams.delete('query');

  const baseUrl = fullUrl.origin ?? 'https://www.mec.ca';

  const trimmedPathname = fullUrl.pathname.replace(/\/$/, '');

  const serverUrl = `${baseUrl}${trimmedPathname}${
    strippedQuery.toString() ? `?${strippedQuery.toString()}` : ''
  }`;
  const existingUserToken = req.cookies._ALGOLIA;
  const userToken = existingUserToken || `anonymous-${uuidv4()}`;

  if (!existingUserToken) {
    const existingSetCookie = res.getHeader('Set-Cookie');
    const setCookieHeader = Array.isArray(existingSetCookie)
      ? existingSetCookie
      : existingSetCookie
      ? [String(existingSetCookie)]
      : [];

    setCookieHeader.push(
      `_ALGOLIA=${encodeURIComponent(
        userToken
      )}; Path=/; HttpOnly; SameSite=Lax; Secure`
    );

    res.setHeader('Set-Cookie', setCookieHeader);
  }

  const { searchKey: algoliaSearchKey, expiresAt: algoliaSearchKeyExpiresAt } =
    generateAlgoliaSearchKey(userToken);

  // Resolve the customer's pricing context server-side so price sort/filter
  // attributes are correct in the SSR markup and on the first client render
  // (avoids the guest→tier flip + extra Algolia query on hard reload). Guests
  // have no customer token, so this returns null without a network call.
  let priceGroupKey: PriceGroupKey = 'guest';
  try {
    const loggedInCustomer = await getLoggedInCustomer(req);
    if (loggedInCustomer?.customerGroupId) {
      priceGroupKey = getGroupKeyByCustomerGroupId(
        loggedInCustomer.customerGroupId
      );
    }
  } catch (error) {
    console.error('Failed to resolve pricing group for SSR', error);
  }

  const serverState = await getServerState(
    <LocaleOverrideContext.Provider value={notDefaultLocale}>
      <SsrPriceGroupContext.Provider value={priceGroupKey}>
        <SearchResults
          serverUrl={serverUrl}
          locale={notDefaultLocale}
          isMobile={isMobile}
          title={''}
          pttTiles={[]}
          productLineCarousel={null}
          userToken={userToken}
          algoliaSearchKey={algoliaSearchKey}
          productStripDeals={null}
          dealsGrid={null}
        />
      </SsrPriceGroupContext.Provider>
    </LocaleOverrideContext.Provider>,
    { renderToString }
  );

  const indexName = getIndex(notDefaultLocale);
  const searchResults = serverState?.initialResults[indexName]?.results;
  const searchHits = searchResults?.[0]?.hits || [];
  const algoliaQueryID = searchResults?.[0]?.queryID || null;

  const canFetchPrismicContent =
    collectionSlug &&
    (pathIsActiveCategory || isBrandPlp || isCollectionPlp) &&
    searchHits.length !== 0 &&
    notDefaultLocale;

  const [
    stores,
    promoMessages,
    bannerStripData,
    serverSideTranslation,
    breadcrumbs,
    collectionData,
    seoComponentData,
    productTagInfoData,
    promoBarData,
  ] = await Promise.allSettled([
    getAllStoresDataForSetMyStore(notDefaultLocale),
    !FEATURE_FLAG_PROMO_BAR_SERVER_SIDE &&
      notDefaultLocale &&
      getAllPromoTickerData(notDefaultLocale),
    getBannerStripGlobalData(
      notDefaultLocale,
      preview ? previewRef : undefined
    ),
    serverSideTranslations(notDefaultLocale, [
      'common',
      'searchResults',
      'promotions',
      'comparison',
      'plp',
      'quickAdd',
    ]),
    getCategoryBreadcrumbs(pageLocale, categoryPathWithoutFilters),
    canFetchPrismicContent
      ? await getCollectionByCategoryPath(
          cacheableCollectionPath,
          pageLocale,
          req,
          preview ? previewRef : undefined,
          process.env.NEXT_PUBLIC_ENABLE_LOGGING === 'true'
        )
      : null,
    canFetchPrismicContent
      ? getSeoComponentByUid(
          `${collectionSlug.replace(/\//g, '')}${strippedQuery
            .toString()
            .toLocaleLowerCase()
            .replace(/%/g, '')
            .replace(/=/g, '')
            .replace(/&/g, '')}`,
          notDefaultLocale
        )
      : null,
    canFetchPrismicContent ? getProductTagInfoData(notDefaultLocale) : null,
    FEATURE_FLAG_PROMO_BAR_SERVER_SIDE &&
      notDefaultLocale &&
      getPromoBarData(notDefaultLocale),
  ]).then((results) =>
    results.map((promise) => {
      if (
        promise.status === 'rejected' ||
        typeof promise.value === 'undefined'
      ) {
        console.error('promise rejected or returning undefined', promise);
        return null;
      }
      return promise.value;
    })
  );

  const errorPageProps = {
    stores,
    promoMessages,
    promoBarData,
    bannerStripData,
    locale: notDefaultLocale,
    ...(await serverSideTranslations(notDefaultLocale, [
      'common',
      'searchResults',
      'promotions',
    ])),
  };

  const categoryNotFound =
    (!pathIsActiveCategory && !isCollectionPlp) ||
    (pathIsActiveCategory && !searchHits.length);

  if (categoryNotFound) {
    console.warn(`PLP404 ${categoryPath}`);
    res.statusCode = 404;
    return {
      props: errorPageProps,
    };
  }

  // Build the shape that matches the render path chosen by the flag. Both this
  // and the render read the same env constant, so they stay in sync.
  let seoContentData: SeoAccordionData | SeoContentData | null;
  if (FEATURE_FLAG_SEO_ACCORDION) {
    seoContentData = mapSeoAccordionData(seoComponentData);
  } else {
    const htmlBodyForSEOComponent = prismic.asHTML(seoComponentData?.html_body);
    seoContentData = {
      title: seoComponentData?.title ?? null,
      title_position: seoComponentData?.title_position ?? null,
      body: seoComponentData?.body?.[0]?.text ?? null,
      htmlBody:
        (htmlBodyForSEOComponent &&
          DOMPurify.sanitize(htmlBodyForSEOComponent)) ??
        null,
    };
  }

  let canonicalUrl = '';
  if (notDefaultLocale && splitUrl?.[0]) {
    if (shouldCanonicalToBaseBrand(brandIdentifier, isFilteredBrand)) {
      // ED-304: deep/filtered pages under a targeted brand consolidate ranking
      // signals onto the main brand PLP — point canonical at the clean base
      // brand path with no sub-path or query params.
      canonicalUrl = `${
        process.env.BASE_URL
      }/${notDefaultLocale}${baseBrandCanonicalPath(
        brandIdentifier as string,
        notDefaultLocale
      )}`;
    } else {
      const pageParam = queryString.get('page');
      const canonicalQueryString = pageParam
        ? `?page=${encodeURIComponent(pageParam)}`
        : '';
      // splitUrl[0] is derived from resolvedUrl, which already includes the
      // locale prefix (e.g. /en/products/...), so do NOT prepend the locale
      // again here or it doubles to /en/en/... (ED-306).
      canonicalUrl = `${process.env.BASE_URL}${splitUrl[0]}${canonicalQueryString}`;
    }
  }

  const pathWithoutLocale = splitUrl?.[0];
  const alternateLocaleUrl = generateAltURL(
    pathWithoutLocale,
    notDefaultLocale,
    req
  );

  const { categorySlug: categorySlugs } = getCategorySlugsFromUrl(
    trimmedPathname,
    notDefaultLocale
  );

  const fallbackCategoryTitles: { [key: string]: { [key: string]: string } } = {
    'all-products': {
      en: 'All Products',
      fr: 'Tous les produits',
    },
  };

  const categorySlug = categorySlugs?.at(-1) || '';
  const categoryTitleRaw = categorySlug
    ? getCategoryNameFromSlug(categorySlug, notDefaultLocale)
    : fallbackCategoryTitles[collectionSlug]?.[notDefaultLocale] || '';
  const categoryTitle = isCollectionPlp
    ? categoryTitleRaw.replace(/-/g, ' ')
    : categoryTitleRaw;

  // use BC category title, if available
  const startingPageTitle =
    breadcrumbs?.pageTitle || categoryTitle.toLocaleLowerCase();

  // Derive the H1 from `serverUrl` (sanitized, value-preserving query) rather
  // than the raw `req.url`/`fullUrl.href`. The PLP middleware sanitization step
  // (`NextResponse.rewrite` in proxy.ts) rewrites the request whenever an
  // unknown query param is present (e.g. email/UTM tracking), and the rewritten
  // `req.url` drops the `facets.Ideal for` value on prod (ED-299). `serverUrl`
  // is built from `strippedQuery`, the same reliable source that drives Algolia.
  let getH1AndPageTitleByLocale;
  if (req?.url) {
    getH1AndPageTitleByLocale =
      pageLocale === 'fr'
        ? getFrH1AndPageTitle(serverUrl, startingPageTitle)
        : getEnH1AndPageTitle(serverUrl, startingPageTitle);
  }

  // H1 fallback text. The `<title>` is built separately so merch's collection
  // page title never leaks into the H1, which lives on the hero (ED-449).
  const pageTitle = getH1AndPageTitleByLocale;

  // Merch's page title is used verbatim, and only on the unfiltered PLP;
  // filtered URLs keep the BC title plus facet modifiers.
  const collectionPageTitle = collectionData?.pageTitle?.trim();
  const seoPageTitle =
    collectionPageTitle && !strippedQuery.toString()
      ? collectionPageTitle
      : getH1AndPageTitleByLocale;

  // The PLP hero H1 is display-only and never feeds the `<title>`.
  const plpHeroData: PlpHeroData | null =
    FEATURE_FLAG_PLP_HERO && collectionData?.plpHeroData
      ? {
          ...collectionData.plpHeroData,
          h1: collectionData.plpHeroData.h1 || getH1AndPageTitleByLocale,
        }
      : null;

  if (plpHeroData && collectionData) {
    collectionData.plpBannerData = null;
  }

  const heroTitle =
    plpHeroData?.h1 ||
    (!FEATURE_FLAG_HIDE_LEGACY_PLP_COLLECTION_CONTENT &&
      (collectionData?.brandedStoreSlices?.find(
        (slice: { slice_type: BrandedStoreSliceType }) =>
          slice.slice_type === BrandedStoreSliceType.hero
      )?.primary?.title ||
        collectionData?.enhancedPlpBanner?.title)) ||
    null;

  // Cross-linking band (ED-211). Prismic supplies the ordered product codes from
  // its long-lived cache; the records are read from the products index here, per
  // request, so prices and member pricing match the grid rather than going stale.
  // Codes the index does not know are dropped so one typo cannot blank the band.

  const crossLinkingCms: PlpCrossLinkingCmsData | null =
    collectionData?.plpCrossLinking ?? null;
  let crossLinking: PlpCrossLinkingData | null = null;

  if (crossLinkingCms) {
    const records = await getProductsByProductCodes(
      collectPlpCrossLinkingProductIds(crossLinkingCms.items),
      notDefaultLocale
    );

    const cards = crossLinkingCms.items.flatMap((item) => {
      const record = records.get(item.product_id);

      return record ? [{ record, variantSku: item.variant_sku }] : [];
    });

    crossLinking = cards.length > 0 ? { ...crossLinkingCms, cards } : null;
  }

  const breadcrumbsList = breadcrumbs?.breadcrumbsList;
  const topLevelCategoryId =
    notDefaultLocale === 'fr'
      ? ALGONOMY_TOP_CATEGORY_FR
      : ALGONOMY_TOP_CATEGORY_EN;

  const IS_ALGONOMY_PATH_CAT_ID_ENABLED = getAlgonomyPathFeatureFlag(
    req as NextApiRequest
  );

  const algonomyCategoryId = IS_ALGONOMY_PATH_CAT_ID_ENABLED
    ? `${trimmedPathname.replace(
        `/${notDefaultLocale}/${notDefaultLocale}/`,
        `/${notDefaultLocale}/`
      )}/`.replace(/\/\//g, '/')
    : breadcrumbsList?.at(-1)?.entityId?.toString() ?? topLevelCategoryId;

  // x-default points to the default-locale (EN) version of THIS page on the
  // production domain (EN page → its canonical; FR page → alternateLocaleUrl).
  // It should match canonical/hreflang patterns (no filter params; pagination may include ?page=).
  // Never serverUrl, which is the req-host (bc-prod on Vercel) URL with query params,
  // kept only for InstantSearch SSR routing.
  const xDefault =
    notDefaultLocale === 'en' ? canonicalUrl : alternateLocaleUrl;

  return {
    props: {
      stores,
      xDefault,
      canonicalUrl,
      alternateLocaleUrl,
      ...serverSideTranslation,
      promoMessages,
      bannerStripData,
      serverState,
      serverUrl,
      locale: notDefaultLocale,
      pageTitle,
      seoPageTitle,
      collectionData,
      algonomyCategoryId,
      seoContentData,
      isMobile,
      productTagInfoData,
      collectionSlug,
      heroTitle,
      plpHeroData,
      crossLinking,
      algoliaQueryID,
      promoBarData,
      priceGroupKey,
      algoliaSearchKey: {
        appId: process.env.NEXT_PUBLIC_ALGOLIA_APP_ID || '',
        searchKey: algoliaSearchKey,
        userToken,
        expiresAt: algoliaSearchKeyExpiresAt,
      },
    },
  };
}
